import { Box, Button, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { QuestionProps } from '@/models/Question';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';

import DataUploadWrap from '../application_components/question_types/data_upload/DataUploadWrap';
import TextTypeQuestions from '../application_components/question_types/TextTypeQuestions';
import ConfirmationModal from '../common/ConfirmationModal';
import RequiredIndicator from '../common/RequiredIndicator';

export default function GrowthForm({
  questions,
  postInputs,
  userType,
  appStatus,
  activity,
  deleteActivity,
}: {
  questions: QuestionProps[];
  postInputs: any;
  userType?: any;
  appStatus?: ApplicationStatus;
  activity?: any;
  deleteActivity?: (activity: any) => Promise<void>;
}) {
  const { answers, canEdit } = useContext(CurrentApplicationContext);

  const [questionAnswers, setQuestionAnswers] = useState<any>();
  const [questionError, setQuestionError] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deletingActivity, setDeletingActivity] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [textQuestion, setTextQuestion] = useState<QuestionProps>();
  const {
    register,
    unregister,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      url: '',
      upload: '',
      uploadSwitch: '',
    },
  });

  const watchUploadSwitch = watch('uploadSwitch');

  useEffect(() => {
    if (questions && questions.length > 0) {
      setTextQuestion(questions[0]);
    }
  }, [questions]);

  useEffect(() => {
    if (watchUploadSwitch) {
      register('url');
      unregister('upload');
    } else {
      unregister('url');
      register('upload');
    }
  }, [register, unregister, watchUploadSwitch]);

  const checkAnswerFields = useCallback(() => {
    const answersValid = Object.values(questionAnswers).every((questionAnswer: any) => {
      const coorespondingQuestion = questions.find((q: any) => q.id == questionAnswer.questionId);

      if (!coorespondingQuestion?.questionNeeded) return true;

      if (questionAnswer.questionType == 'textbox' && !questionAnswer.responseString) {
        return false;
      }

      if (questionAnswer.questionType == 'upload' && questionAnswer?.files?.length == 0) {
        return false;
      }

      return true;
    });

    setQuestionError(!answersValid);
    return answersValid;
  }, [questionAnswers, questions]);

  useEffect(() => {
    if (answers && questions) {
      const questionIds = questions.map((question: any) => `${question.id}`);

      const updatedQuestionAnswers = Object.fromEntries(
        Object.entries(answers).filter(([key]) => questionIds.includes(key))
      );

      setQuestionAnswers(updatedQuestionAnswers);
    }
  }, [answers, questions]);

  useEffect(() => {
    if (questionAnswers) {
      checkAnswerFields();
    }
  }, [checkAnswerFields, questionAnswers]);

  const handleDeleteActivity = useCallback(() => {
    if (!activity) return;

    setDeletingActivity(true);
    if (deleteActivity) {
      deleteActivity(activity).finally(() => {
        setDeletingActivity(false);
      });
    }
  }, [activity, deleteActivity]);

  const openDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const submitForm = () => {
    setSubmitting(true);
    postInputs(questions, questionAnswers).finally(() => {
      setSubmitting(false);
    });
  };

  const uploadFileCheck = (file: boolean) => {
    if (file) {
      setQuestionAnswers({
        ...questionAnswers,
        file: true,
      });
    }
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        textAlign: 'left',
        marginBottom: '20px',
      }}
    >
      {textQuestion != null && (
        <TextTypeQuestions key={textQuestion.activityId} question={textQuestion} />
      )}

      {errors.text && (
        <Typography variant='subtitle3' color='error'>
          This field is required
        </Typography>
      )}
      {questions[1]?.questionType != 'upload' && (
        <label>
          {questions[1]?.questionText} {questions[1]?.questionNeeded && <RequiredIndicator />}
        </label>
      )}

      {questions != null && (
        <DataUploadWrap question={questions[1]} eventUploadFileCheck={uploadFileCheck} />
      )}
      {((userType == UserType.Applicant &&
        appStatus != null &&
        [ApplicationStatus.InProgress, ApplicationStatus.InRevision].includes(appStatus)) ||
        userType != UserType.Applicant) && (
        <Box display='flex' justifyContent='center' gap='40px'>
          <Button
            variant='primary'
            size='large'
            onClick={() => {
              submitForm();
            }}
            disabled={submitting || questionError}
          >
            Submit Activity
          </Button>
          {deleteActivity != null && (
            <Button
              style={{ marginTop: '2px' }}
              variant='secondary'
              size='large'
              disabled={deletingActivity || !canEdit}
              onClick={openDeleteConfirmation}
            >
              Delete Activity
            </Button>
          )}
        </Box>
      )}
      <ConfirmationModal
        open={confirmDeleteOpen}
        onClose={closeDeleteConfirmation}
        header='Delete Activity'
        confirmationText='Are you sure you want to delete this activity? This action cannot be undone.'
        confirmButtonText='Delete Activity'
        onDelete={handleDeleteActivity}
      />
    </Box>
  );
}
